/**
 * c-router.js — SPA client-side router for ANGELtype / Etheryx theme
 *
 * Intercepts internal link clicks, fetches the target page, and swaps
 * only #mainContent — leaving the header, footer, cart drawer, and all
 * overlay sections untouched.
 *
 * Builds on existing infrastructure:
 *  - m-instantpage.js  : prefetches pages into the browser HTTP cache;
 *                        our fetch() calls benefit automatically.
 *  - c-facets.js       : manages its own URL & popstate — excluded below.
 *  - infinite-paginate : manages its own click & fetch — excluded below.
 *  - m-cart.js         : lives in #overlayGroup, never touched.
 *  - app.js globalTasks: re-called after every swap.
 *
 * Dispatches: document → CustomEvent('spa:navigated', { detail: { url, title } })
 * Escape hatch: add data-no-spa to any <a> to opt it out.
 *
 * Loading state: #mainContent gets [data-loading] during the fetch so the
 * page dims slightly (opacity:0.85) to signal activity. The clicked product
 * card also gets [spa-loading] for a tactile image-level response.
 */

/** External script src values that have already been executed. Prevents
 *  re-running module-scoped scripts (e.g. t-product.js) on every SPA swap,
 *  which would cause "Identifier already declared" errors.
 */
const _injectedScriptSrcs = new Set();

// ---------------------------------------------------------------------------
// CSS injection — card tactile loading feedback
// ---------------------------------------------------------------------------
// Idempotent (style id guard). Gated on enableAnimations: tactile feedback
// is a motion affordance so it respects the master animations toggle.

if (window.themeSettings?.enableAnimations !== false) {
  if (!document.getElementById('c-router-styles')) {
    const style = document.createElement('style');
    style.id = 'c-router-styles';
    style.textContent = `
      /* Immediate visual response when a product card is tapped/clicked.
         Applied for the duration of the fetch, cleared before VT captures
         the old state so the snapshot sees the normal card image. */
      product-card[spa-loading] media-loader {
        opacity: 0.72;
        transition: opacity 120ms ease;
      }
    `;
    document.head.appendChild(style);
  }
}

class SpaRouter {
  constructor() {
    /** @type {Map<string, {html:string, title:string, coretexPage:string, coretexPath:string}>} */
    this._cache = new Map();
    this._navigating = false;
    /** Timestamp of the last accepted click — guards against rapid double-clicks. */
    this._lastClick = 0;

    // Stamp the initial history entry so popstate can always restore it.
    history.replaceState(
      {
        scrollY: 0,
        url: location.href,
        spa: true,
        // Include searchParams so c-facets' popstate guard (searchParams === searchParamsPrev)
        // has a defined value to compare against when we push our own history entries.
        searchParams: location.search.slice(1),
      },
      '',
      location.href
    );

    document.addEventListener('click', this._handleClick.bind(this), false);
    window.addEventListener('popstate', this._handlePopstate.bind(this));
  }

  // ---------------------------------------------------------------------------
  // Click handling
  // ---------------------------------------------------------------------------

  _handleClick(event) {
    // When the menu drawer is open, only intercept clicks that originate INSIDE
    // the drawer (menu links the user intentionally clicked). Block clicks from
    // outside — those are part of the drawer's body-click-close lifecycle and
    // must not trigger SPA navigation while the drawer is mid-lifecycle.
    const _menuEl = document.getElementById('menuDrawerBox');
    if (_menuEl?.hasAttribute('open') && !_menuEl.contains(event.target)) return;
    // Let components that already called preventDefault keep control.
    if (event.defaultPrevented) return;
    // Modifier keys: user wants to open in new tab / window / etc.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    // Not a primary (left) click.
    if (event.button !== 0) return;

    const link = event.target.closest('a[href]');
    if (!link || !this._isNavigable(link)) return;

    // Debounce rapid double-clicks — the _navigating flag handles concurrent
    // navigations, but a 300ms window here catches accidental repeat clicks
    // before the first navigation even starts (e.g. on a slow connection).
    const now = Date.now();
    if (now - this._lastClick < 300) return;
    this._lastClick = now;

    event.preventDefault();

    // Snapshot current scroll before leaving this page.
    history.replaceState(
      { ...history.state, scrollY: window.scrollY },
      '',
      location.href
    );

    // Tactile loading feedback: dim the card image while the fetch runs.
    link.closest('product-card')?.setAttribute('spa-loading', '');

    this._navigate(link.href, { push: true, scrollY: 0, sourceEl: link });
  }

  _isNavigable(link) {
    // Must be same origin.
    if (link.origin !== location.origin) return false;
    // Explicit target other than the current frame.
    if (link.target && link.target !== '_self' && link.target !== '_top') return false;
    // File download.
    if (link.hasAttribute('download')) return false;
    // Hash-only navigation — just scroll, don't swap.
    if (link.hash && link.pathname + link.search === location.pathname + location.search) return false;
    // Non-HTTP(S) protocols (mailto:, tel:, javascript:, etc.).
    if (!['http:', 'https:'].includes(link.protocol)) return false;
    // Explicit opt-out.
    if ('noSpa' in link.dataset) return false;
    // Cart section links — m-cart.js owns these.
    if (link.closest('[data-ajax-cart-section]')) return false;
    // infinite-paginate manages its own click → fetch flow.
    if (link.closest('infinite-paginate')) return false;
    // facets-form manages its own click → fetch flow (and calls preventDefault).
    if (link.closest('facets-form')) return false;

    return true;
  }

  // ---------------------------------------------------------------------------
  // Popstate (back / forward)
  // ---------------------------------------------------------------------------

  _handlePopstate() {
    // Skip popstate events that fire while the menu drawer is open — the drawer
    // may push/pop its own history entry as part of its open/close cycle.
    if (document.getElementById('menuDrawerBox')?.hasAttribute('open')) return;
    // location.href has already been updated to the target URL by the browser.
    const scrollY = history.state?.scrollY ?? 0;
    this._navigate(location.href, { push: false, scrollY });
  }

  // ---------------------------------------------------------------------------
  // Core navigation
  // ---------------------------------------------------------------------------

  async _navigate(url, { push = true, scrollY = 0, sourceEl = null } = {}) {
    // Prevent concurrent navigations; the first one wins.
    if (this._navigating) return;
    this._navigating = true;

    // Dim #mainContent while the fetch is in-flight so the user sees activity.
    document.getElementById('mainContent')?.setAttribute('data-loading', '');

    try {
      const page = await this._fetchPage(url);

      if (push) {
        history.pushState(
          {
            scrollY: 0,
            url,
            spa: true,
            searchParams: new URL(url, location.origin).search.slice(1),
          },
          '',
          url
        );
      }

      // Fire before the swap so listeners can stamp view-transition-name on source
      // elements while the "old" DOM state is still live.
      document.dispatchEvent(new CustomEvent('spa:before-swap', {
        detail: { url, sourceEl },
      }));

      // The actual DOM mutation — also fires spa:after-swap so listeners can stamp
      // the destination element before the browser captures the "new" state.
      // Scroll is applied here (synchronously with the swap) so it is never
      // deferred past an async await vt.ready gap.
      const performSwap = () => {
        this._swap(page);
        window.scrollTo({ top: scrollY, behavior: 'instant' });
        if (scrollY === 0) {
          // Second scroll in the next frame to override any browser hash-anchor
          // re-scroll that fires asynchronously after the URL is committed.
          requestAnimationFrame(() => {
            window.scrollTo({ top: 0, behavior: 'instant' });
            // If the top h1 is obscured by the sticky header, scroll down to reveal it.
            const h1 = document.querySelector('#mainContent h1');
            if (h1) {
              const headSpace = parseFloat(
                getComputedStyle(document.documentElement).getPropertyValue('--headSpace') || '0'
              );
              const rect = h1.getBoundingClientRect();
              if (rect.top >= 0 && rect.top < headSpace) {
                window.scrollTo({ top: headSpace - rect.top, behavior: 'instant' });
              }
            }
          });
        }
        document.dispatchEvent(new CustomEvent('spa:after-swap', { detail: { url } }));
      };

      // Clear the card loading feedback before the swap.
      document.querySelectorAll('product-card[spa-loading]').forEach((el) =>
        el.removeAttribute('spa-loading')
      );

      // Temporarily pin all product cards to their final visible state so they
      // are never painted in the hidden (opacity:0 / translateY) starting state
      // during the swap frame. Removed after two frames so below-fold
      // IntersectionObserver animations still fire normally.
      const _noAnimStyle = document.createElement('style');
      _noAnimStyle.textContent =
        '#ajaxContainer product-card { opacity: 1 !important; transform: none !important; transition: none !important; }';
      document.head.appendChild(_noAnimStyle);

      performSwap();

      requestAnimationFrame(() => requestAnimationFrame(() => _noAnimStyle.remove()));

      document.dispatchEvent(
        new CustomEvent('spa:navigated', {
          bubbles: true,
          detail: { url, title: page.title },
        })
      );
    } catch (err) {
      console.error('[c-router] Navigation failed — falling back to full page load:', err);
      // Hard redirect is the safest fallback; never leave the user on the wrong page.
      window.location.href = url;
    } finally {
      this._navigating = false;
      document.getElementById('mainContent')?.removeAttribute('data-loading');
      // Safety net: clear any remaining card loading states (e.g. on error path).
      document.querySelectorAll('product-card[spa-loading]').forEach((el) =>
        el.removeAttribute('spa-loading')
      );
    }
  }

  // ---------------------------------------------------------------------------
  // Fetch & cache
  // ---------------------------------------------------------------------------

  async _fetchPage(url) {
    const key = new URL(url, location.origin).href;

    if (this._cache.has(key)) return this._cache.get(key);

    const response = await fetch(key);
    if (!response.ok) throw new Error(`HTTP ${response.status} fetching ${key}`);

    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');

    const mainEl = doc.getElementById('mainContent');
    if (!mainEl) throw new Error(`[c-router] #mainContent not found in response for ${key}`);

    const page = {
      html: mainEl.innerHTML,
      title: doc.title,
      coretexPage: doc.body.getAttribute('coretex-page') || '',
      coretexPath: doc.body.getAttribute('coretex-path') || '',
    };

    this._cache.set(key, page);
    return page;
  }

  // ---------------------------------------------------------------------------
  // DOM swap & re-initialisation
  // ---------------------------------------------------------------------------

  _swap(page) {
    // 1. Update document title and URL-reflected body attributes.
    document.title = page.title;
    if (page.coretexPage) document.body.setAttribute('coretex-page', page.coretexPage);
    if (page.coretexPath) document.body.setAttribute('coretex-path', page.coretexPath);

    // 2. Replace main content.
    const main = document.getElementById('mainContent');
    if (!main) return; // Should never happen, but guard against race conditions.
    main.innerHTML = page.html;

    // 3b. Re-execute scripts inside the new content.
    //    Browsers intentionally skip scripts inserted via innerHTML; we clone
    //    them so they run in the current document context.
    //
    //    External scripts (src=): skip if already executed — re-running them
    //    would cause "Identifier already declared" errors for top-level const/let.
    //    Inline scripts: wrap in an IIFE so their top-level declarations are
    //    block-scoped and cannot conflict on re-execution.
    main.querySelectorAll('script').forEach((oldScript) => {
      if (oldScript.src) {
        // Skip externals that have already been injected by a previous swap.
        if (_injectedScriptSrcs.has(oldScript.src)) { oldScript.remove(); return; }
        _injectedScriptSrcs.add(oldScript.src);
      }
      const newScript = document.createElement('script');
      [...oldScript.attributes].forEach((attr) =>
        newScript.setAttribute(attr.name, attr.value)
      );
      if (!oldScript.src) {
        newScript.textContent = `(function(){\n${oldScript.textContent}\n})();`;
      }
      oldScript.replaceWith(newScript);
    });

    // 3c. Re-run theme global tasks (recalculates header/group CSS vars,
    //    viewport classes, drawer-footer aria state, etc.).
    //    globalTasks() is defined globally in app.js.
    if (typeof globalTasks === 'function') globalTasks();
  }
}

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

document.addEventListener('DOMContentLoaded', () => {
  // The Shopify theme editor reloads sections via its own mechanisms;
  // SPA navigation would interfere with editor previews.
  if (window.Shopify?.designMode) return;

  window.spaRouter = new SpaRouter();
});

// ---------------------------------------------------------------------------
// Back button — inject into #mainContent on product pages reached via SPA
// ---------------------------------------------------------------------------

let _prevPathname = location.pathname;

document.addEventListener('spa:navigated', () => {
  // Guard: if pathname hasn't changed (facet filter update, same-page popstate, etc.)
  // skip all side effects entirely — this is what causes the menu to close on itself.
  if (location.pathname === _prevPathname) return;
  _prevPathname = location.pathname;

  try {
    // Close desktop dropdown menus on every navigation.
    document.querySelectorAll('#headerGroup details.lv1[open]').forEach((d) => { d.open = false; });

    // Close the menu drawer only if it is actually open — explicit check prevents
    // interfering with the drawer's own state management when it's already closed.
    const _menuDrawer = document.getElementById('menuDrawerBox');
    if (_menuDrawer?.hasAttribute('open')) _menuDrawer.close();

    const onProductPage = location.pathname.includes('/products/');
    const arrivedViaSpa = history.state?.spa === true;

    // Remove any existing button (navigation away from product page,
    // or a subsequent SPA navigation that re-runs this handler).
    document.getElementById('c-back-btn')?.remove();

    if (onProductPage && arrivedViaSpa) {
      const btn = document.createElement('button');
      btn.id = 'c-back-btn';
      btn.setAttribute('aria-label', 'Back');
      btn.textContent = '‹';
      btn.addEventListener('click', () => history.back());
      // Append to body so nothing in #mainContent (product image, etc.) can intercept taps.
      document.body.appendChild(btn);
      // Trigger the opacity transition on the next frame so the browser
      // captures the initial opacity:0 state before animating to 1.
      requestAnimationFrame(() => btn.classList.add('is-visible'));
    }
  } catch (err) {
    console.error('[c-router] spa:navigated handler error:', err);
  }
});
